import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type {
  AttributeProfileResponse,
  EmployeeAchievementsResponse,
  EmployeeExperienceResponse,
  EmployeeProfileResponse,
  EmployeeQualificationsResponse,
  EmployeeSkillsResponse,
  RoleListResponse,
} from '../api/types';
import { ApiError, describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { ProfileHeader } from '../components/profile/ProfileHeader';
import { ProfileQuickNav } from '../components/profile/ProfileQuickNav';
import { EmployeeProfileModeSwitch } from '../components/profile/EmployeeProfileModeSwitch';
import { EmployeeSnapshot } from '../components/profile/EmployeeSnapshot';
import { AttributesSection } from '../components/profile/AttributesSection';
import { FactSection } from '../components/profile/FactSection';
import { SkillsSection } from '../components/profile/SkillsSection';
import { ExperienceSection } from '../components/profile/ExperienceSection';
import { QualificationsSection } from '../components/profile/QualificationsSection';
import { AchievementsSection } from '../components/profile/AchievementsSection';
import { DocumentsSection } from '../components/profile/DocumentsSection';
import { TraitSection } from '../components/profile/TraitSection';
import { CapabilitiesSection } from '../components/profile/CapabilitiesSection';
import { RoleFitSection } from '../components/profile/RoleFitSection';
import { PotentialReadinessSection } from '../components/profile/PotentialReadinessSection';
import { TalentDesignationSection } from '../components/profile/TalentDesignationSection';
import { DevelopmentSection } from '../components/profile/DevelopmentSection';
import { CareerAspirationsSection } from '../components/profile/CareerAspirationsSection';
import { RecordSkillControl, RecordQualificationControl, RecordExperienceControl, RecordAchievementControl } from '../components/profile/EmployeeFactsCapture';
import { formatTimestamp } from '../components/profile/attributeDisplay';
import styles from './EmployeeProfilePage.module.css';

/**
 * One person's profile — Snapshot or Detailed, the same route, the same
 * component, the same already-loaded data.
 *
 * ---------------------------------------------------------------------
 * ONE WILDCARD ROUTE, A MODE DERIVED FROM THE URL — NOT TWO ROUTES
 *
 * `App.tsx` mounts this component at `/people/:employeeId/*`, matching both
 * `/people/:id` (Snapshot, the default) and `/people/:id/detailed`. `mode`
 * below is read straight from `useLocation()` rather than from a second
 * `<Route>` pointing at this same element: two Route entries would make
 * React Router treat a Snapshot<->Detailed switch as navigating to a
 * genuinely different element, unmounting and remounting this whole page —
 * discarding `profile`/`attributes` and re-issuing every request that
 * backs them, the exact "avoid duplicate API requests" Mission 171's own
 * brief (§19) asks not to do. Reading the URL instead means `useApiResource`
 * state survives a mode switch untouched; only what renders changes, and
 * old `/people/:id` links still land exactly where they always did.
 *
 * `profile` and `attributes` are fetched unconditionally — both modes need
 * the header, and the Snapshot's attribute-profile radar needs the same
 * full attribute list Detailed's own Attributes section does, so fetching
 * it once here and passing it down (`EmployeeSnapshot`'s own header
 * comment) is the one exception to every other resource on this page being
 * fetched only when its own mode needs it. Skills/Experience/
 * Qualifications/Achievements/`roles` — and every self-contained Detailed-
 * only section below (Documents, Traits, Capabilities, Role Fit,
 * Potential/Readiness, Talent Designation, Development, Career) — are
 * requested only in Detailed mode: the facts-section hooks take a `null`
 * path in Snapshot mode (the same "an inert hook, not a conditional hook
 * call" pattern `RoleFitSection`'s own `currentRole`-gated request already
 * uses), and the self-contained sections simply are not mounted at all,
 * so they never fetch anything to begin with.
 *
 * OVERVIEW IS GONE FROM HERE — IT BECAME THE SNAPSHOT
 *
 * `OverviewSection` (Mission 020) answered exactly the question Snapshot
 * now answers ("who is this person, built entirely from data that exists
 * elsewhere") one mission earlier. Keeping both would have been the same
 * summary shown twice on the same person — `EmployeeSnapshot.tsx`'s own
 * header comment has the full account of why it was retired rather than
 * duplicated, not merely hidden.
 *
 * MANY INDEPENDENT REQUESTS RATHER THAN ONE
 *
 * A single `/employees/:id/profile` aggregate would be fewer round trips,
 * and it was considered and not built. It would put a response shape in the
 * API whose only justification is this page's current layout, and it would
 * couple every future change of layout to a backend change. Independent
 * requests also fail independently, which is the behaviour this page wants:
 * a failing Skills request must not cost the reader the attributes, and
 * vice versa. Development and Career each own their own request too
 * (`DevelopmentSection`/`CareerAspirationsSection` call `useApiResource`
 * themselves, the same way `OverviewSection`/`RoleFitSection` already do) —
 * this file only fetches what more than one section needs, which is why
 * `roles` is fetched once here and passed down to both. If profile load
 * time ever becomes a real measured problem, that is the moment to revisit
 * it — with a measurement, not an assumption (docs/API.md §10).
 *
 * SECTION ORDER (Detailed mode)
 *
 * Attributes, then Skills, Experience, Qualifications, Achievements, then
 * Role Fit, then Potential/Readiness, then Talent Designation, then
 * Development, then Career — §3.3-§3.4's order followed by §3.6-§3.8's,
 * broadly decreasing "glance value" (Overview's own former place in this
 * ordering, §3.2, is now Snapshot's job instead). The facts sections
 * deliberately sit below Attributes and look nothing like them: see
 * SkillsSection's note on why a proficiency must not be drawn the way a
 * composite rating is. Talent Designation sits beside Potential/Readiness
 * rather than beside the facts sections above, despite reusing their
 * shared styling (`FactRecords.module.css`) — docs/PRODUCT_CONSTITUTION.md
 * §8/§12 groups Potential, Readiness, Trajectory, Aspiration and Talent
 * Designation as one family of explicit human judgements, and this page's
 * layout follows that grouping, not the visual shell a section happens to
 * reuse.
 * ---------------------------------------------------------------------
 */
export function EmployeeProfilePage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const base = employeeId ? `/employees/${employeeId}` : null;

  // `/people/:id` -> snapshot, `/people/:id/detailed` -> detailed — read
  // from the URL every render, not stored as component state, so the
  // browser's own back/forward and a typed-in address bar change both work
  // without this page doing anything special for them (this file's own
  // header comment has the full reasoning for why this is one route, not
  // two).
  const location = useLocation();
  const mode: 'snapshot' | 'detailed' = location.pathname.endsWith('/detailed') ? 'detailed' : 'snapshot';
  const detailedBase = mode === 'detailed' ? base : null;

  /**
   * Bumped after an assessment is recorded, to remount Role Fit so it
   * re-reads (Mission 014).
   *
   * Role Fit owns its own request, and its per-requirement outcomes are
   * compared against the very attribute composites an assessment changes —
   * so leaving it on screen unchanged after a write would show a comparison
   * against a value that no longer exists. Remounting is the smallest way
   * to make it ask again; nothing about the new fit is computed here.
   */
  const [dataVersion, setDataVersion] = useState(0);

  // Needed by both modes (the header + Snapshot's attribute-profile radar;
  // Detailed's own Attributes section) — fetched once, unconditionally.
  const profile = useApiResource<EmployeeProfileResponse>(base);
  const attributes = useApiResource<AttributeProfileResponse>(base && `${base}/attributes`);

  // Detailed-only from here down: an inert (`null`-path) hook when
  // Snapshot is showing, exactly as `RoleFitSection`'s own
  // `currentRole`-gated request already does for "nothing to ask yet".
  const skills = useApiResource<EmployeeSkillsResponse>(detailedBase && `${detailedBase}/skills`);
  const experience = useApiResource<EmployeeExperienceResponse>(detailedBase && `${detailedBase}/experience`);
  const qualifications = useApiResource<EmployeeQualificationsResponse>(detailedBase && `${detailedBase}/qualifications`);
  const achievements = useApiResource<EmployeeAchievementsResponse>(detailedBase && `${detailedBase}/achievements`);
  /**
   * The role library, for Development and Career's own target-role fields
   * below. `NewDevelopmentPlanRequest`/`NewCareerAspirationRequest` store
   * only a `targetRoleId` (docs/DOMAIN_DECISIONS.md "Development and Career
   * Aspiration Capture Scope"), and this repository's own convention is
   * that an id is resolved to a name by the server, never guessed by the
   * client (see `RoleRequirementResponse.subjectName`) — but no existing
   * endpoint resolves *this* one id, so this reuses Mission 013's existing,
   * unmodified `GET /roles` (the same request `RolesPage` already makes)
   * rather than inventing a new resolution endpoint for two optional
   * fields in a basic-capture form.
   */
  const roles = useApiResource<RoleListResponse>(mode === 'detailed' ? '/roles' : null);

  function handleAssessmentRecorded() {
    // Re-read, never patch. Every derived figure on this page belongs to
    // the domain, and the only honest way to learn what a new assessment
    // meant is to ask for it again.
    attributes.reload();
    setDataVersion((version) => version + 1);
  }

  if (profile.status === 'loading') return <LoadingState label="Loading profile…" />;

  if (profile.status === 'failed') {
    const notFound = profile.error instanceof ApiError && profile.error.status === 404;
    return (
      <div className={styles.page}>
        <BackLink />
        <ErrorState
          title={notFound ? 'This person could not be found' : 'This profile could not be loaded'}
          detail={
            notFound
              ? 'They may not exist, or they may be outside the people you are able to view.'
              : describeError(profile.error)
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <BackLink />
      <ProfileHeader profile={profile.data} />
      <EmployeeProfileModeSwitch employeeId={profile.data.id} />

      {mode === 'snapshot' ? (
        // Remounted after a write for the same reason Role Fit's own
        // `dataVersion` key does below — the Snapshot's top strengths,
        // development areas, trajectory and Role Fit summary are all
        // derived from the very attribute composites an assessment
        // changes, by the same `GET .../overview` endpoint Role Fit's
        // neighbour, Overview, used to read.
        <EmployeeSnapshot key={`snapshot-${dataVersion}`} profile={profile.data} attributes={attributes} />
      ) : (
        <DetailedProfile
          profile={profile.data}
          attributes={attributes}
          skills={skills}
          experience={experience}
          qualifications={qualifications}
          achievements={achievements}
          roles={roles}
          dataVersion={dataVersion}
          onAssessmentRecorded={handleAssessmentRecorded}
        />
      )}
    </div>
  );
}

/**
 * Detailed — the complete existing profile, unchanged in substance from
 * before Mission 171: every section that was already here still is, in the
 * same order, with the same authorization/write behaviour. Pulled into its
 * own component only so `EmployeeProfilePage`'s own `return` reads as "pick
 * a mode", not as a few hundred lines of JSX with an `if` wrapped around
 * the back half of it.
 */
function DetailedProfile({
  profile,
  attributes,
  skills,
  experience,
  qualifications,
  achievements,
  roles,
  dataVersion,
  onAssessmentRecorded,
}: {
  profile: EmployeeProfileResponse;
  attributes: ReturnType<typeof useApiResource<AttributeProfileResponse>>;
  skills: ReturnType<typeof useApiResource<EmployeeSkillsResponse>>;
  experience: ReturnType<typeof useApiResource<EmployeeExperienceResponse>>;
  qualifications: ReturnType<typeof useApiResource<EmployeeQualificationsResponse>>;
  achievements: ReturnType<typeof useApiResource<EmployeeAchievementsResponse>>;
  roles: ReturnType<typeof useApiResource<RoleListResponse>>;
  dataVersion: number;
  onAssessmentRecorded: () => void;
}) {
  return (
    <>
      <ProfileQuickNav />

      {/* Anchored here, not only on AttributesSection's own <section>, so
          the quick-nav's Attributes link still resolves to something while
          loading, on failure, or when the library is empty. */}
      <div id="attributes">
        {attributes.status === 'loading' ? <LoadingState label="Loading attributes…" /> : null}

        {attributes.status === 'failed' ? (
          <ErrorState title="Attributes could not be loaded" detail={describeError(attributes.error)} />
        ) : null}

        {attributes.status === 'loaded' ? (
          attributes.data.attributes.length === 0 ? (
            <EmptyState
              title="No attributes are defined"
              detail="The attribute library is empty, so there is nothing to assess this person against yet."
            />
          ) : (
            <AttributesSection
              attributes={attributes.data.attributes}
              employeeId={attributes.data.employeeId}
              employeeName={profile.fullName}
              viewerCanAssessAs={profile.viewerCanAssessAs}
              /* Re-read after a write. The attribute profile is the source of
                 every derived figure on this page — composite, confidence,
                 variance, trajectory — and Role Fit compares against those
                 same composites, so both are refreshed. Nothing is patched
                 locally: the domain decides what a new assessment meant. */
              onAssessmentRecorded={onAssessmentRecorded}
            />
          )
        ) : null}
      </div>

      {/* Skills — a recorded current proficiency per skill, held apart from
          Attributes both visually and semantically. */}
      <FactSection
        id="skills"
        title="Skills"
        state={skills}
        loadingLabel="Loading skills…"
        errorTitle="Skills could not be loaded"
        isEmpty={(data) => data.skills.length === 0}
        empty={{
          title: 'No skills recorded',
          detail:
            'Nothing has been recorded against this person in the skill library. That is an absence of records, not a finding about what they can do.',
        }}
        summary={(data) =>
          `${data.skills.length} ${data.skills.length === 1 ? 'skill' : 'skills'} recorded · current as at ${formatTimestamp(data.asOf)}`
        }
        note="Each skill shows the most recently recorded proficiency, stated as a level rather than a rating — a skill is a recorded fact with one author, not the multi-source assessment an attribute is. Years are recorded against the skill itself and imply nothing about the proficiency beside them."
      >
        {(data) => (
          <SkillsSection
            data={data}
            employeeId={data.employeeId}
            viewerCanAssessAs={profile.viewerCanAssessAs}
            onEvidenceAttached={skills.reload}
          />
        )}
      </FactSection>

      {profile.viewerCanAssessAs !== null ? (
        <RecordSkillControl employeeId={profile.id} onRecorded={skills.reload} />
      ) : null}

      {/* Experience — recorded exposure, per area, never summed. */}
      <FactSection
        id="experience"
        title="Experience"
        state={experience}
        loadingLabel="Loading experience…"
        errorTitle="Experience could not be loaded"
        isEmpty={(data) => data.experience.length === 0}
        empty={{
          title: 'No experience recorded',
          detail:
            'No experience records exist for this person yet. That is an absence of records, not an absence of experience.',
        }}
        summary={(data) =>
          `${data.experience.length} ${data.experience.length === 1 ? 'record' : 'records'}`
        }
        note="Recorded exposure to an area of work, one record at a time. These durations are deliberately not added together: recorded periods can overlap, so a career total would overstate them by a different amount for every person. Experience is exposure — it does not by itself establish proficiency, seniority or readiness."
      >
        {(data) => <ExperienceSection data={data} employeeId={profile.id} viewerCanAssessAs={profile.viewerCanAssessAs} onChanged={experience.reload} />}
      </FactSection>

      {profile.viewerCanAssessAs !== null ? (
        <RecordExperienceControl employeeId={profile.id} onRecorded={experience.reload} />
      ) : null}

      {/* Qualifications — every record, expired ones included. */}
      <FactSection
        id="qualifications"
        title="Qualifications"
        state={qualifications}
        loadingLabel="Loading qualifications…"
        errorTitle="Qualifications could not be loaded"
        isEmpty={(data) => data.qualifications.length === 0}
        empty={{
          title: 'No qualifications recorded',
          detail:
            'No qualification records are held for this person. That is an absence of records, not a statement that they hold no qualifications.',
        }}
        summary={(data) => {
          const expired = data.qualifications.filter((q) => q.expired).length;
          return (
            `${data.qualifications.length} ${data.qualifications.length === 1 ? 'record' : 'records'}` +
            (expired > 0 ? ` · ${expired} past the recorded expiry date` : '') +
            ` · as at ${formatTimestamp(data.asOf)}`
          );
        }}
        note="Every record held, including re-certifications and records past their expiry date — an expired qualification is retained history, not something to hide. Expiry and verification are separate facts and neither is combined into a judgement about whether a credential is valid; where a qualification has been held more than once, every record is listed and none is treated as replacing another."
      >
        {(data) => <QualificationsSection data={data} employeeId={profile.id} viewerCanAssessAs={profile.viewerCanAssessAs} onChanged={qualifications.reload} />}
      </FactSection>

      {profile.viewerCanAssessAs !== null ? (
        <RecordQualificationControl employeeId={profile.id} onRecorded={qualifications.reload} />
      ) : null}

      {/* Achievements — recorded outcomes, unranked and unscored. */}
      <FactSection
        id="achievements"
        title="Achievements"
        state={achievements}
        loadingLabel="Loading achievements…"
        errorTitle="Achievements could not be loaded"
        isEmpty={(data) => data.achievements.length === 0}
        empty={{
          title: 'No achievements recorded',
          detail:
            'Nothing has been recorded here. Achievements are recorded when someone chooses to record them, so an empty list says nothing about what this person has done.',
        }}
        summary={(data) =>
          `${data.achievements.length} ${data.achievements.length === 1 ? 'record' : 'records'}, most recent first`
        }
        note="Recorded outcomes, with the context they happened in. They are not rated, ranked or scored, and nothing in the system derives capability or potential from them — what an achievement is worth is a judgement for the reader."
      >
        {(data) => <AchievementsSection data={data} employeeId={profile.id} viewerCanAssessAs={profile.viewerCanAssessAs} onChanged={achievements.reload} />}
      </FactSection>

      {profile.viewerCanAssessAs !== null ? (
        <RecordAchievementControl employeeId={profile.id} onRecorded={achievements.reload} />
      ) : null}

      {/* Documents (Mission 168, "Visual Identity & Employee Media" MVP)
          — a separate concept from Evidence (docs/ROADMAP.md's own
          written boundary), self-contained like TraitSection below it.
          Self, admin, or hr_partner only — never a manager
          (profile.viewerCanManageDocuments, server-decided). */}
      <DocumentsSection profile={profile} />

      {/* Traits (Mission 166, implementing the specification Mission 165
          authorised — docs/DOMAIN_DECISIONS.md "Composite Concepts").
          Self-contained like PotentialReadinessSection/TalentDesignationSection
          (its own useApiResource, its own FactSection, its own Record/
          Correct forms) rather than wired through this page's resource
          list — a broader behavioural pattern, never a rating, never an
          Attribute/Skill/Capability/Talent Designation (see the section's
          own documentation for the full boundary). */}
      <TraitSection profile={profile} />

      {/* Capabilities (Mission 166). Read-only, like Role Fit below it: a
          Capability is Derived/computed, never directly assessed, so this
          section offers no form — see the section's own documentation. */}
      <CapabilitiesSection profile={profile} />

      {/* Role fit last, following docs/UX_ARCHITECTURE.md §3's own ordering
          (§3.6 sits after the fact sections). §3.6 also describes the MVP
          fit summary as living in §3.2's Overview — Mission 013 built this
          full breakdown at its own §3.6 position instead, and the Snapshot
          (Mission 171, carrying the summary Overview used to) links back
          to this section for the detail rather than duplicating it
          (docs/DOMAIN_DECISIONS.md "Profile Overview Role Fit summary
          projection"). */}
      <RoleFitSection key={`role-fit-${dataVersion}`} profile={profile} />

      {/* Potential, then Readiness for the current role — the Potential/
          Readiness Assessment Capture increment (docs/DOMAIN_DECISIONS.md).
          Placed beside Role Fit because Readiness is judged against the
          same current role Role Fit compares against, though the two are
          never computed from each other. Manager/HR/Admin write only
          (`profile.viewerCanRecordPotentialReadiness`); reading is the
          same broad gate every other fact section on this page uses. */}
      <PotentialReadinessSection profile={profile} />

      {/* Talent Designation — write path added Mission 166, implementing
          the specification Mission 165 authorised
          (docs/DOMAIN_DECISIONS.md "Composite Concepts"). Now
          self-contained like `PotentialReadinessSection` (its own
          `useApiResource` call and its own internal `FactSection`),
          rather than wired through this page's own resource list, because
          it now needs its own reload trigger after a write — the same
          restructuring reason `PotentialReadinessSection` is its own file.
          Authority reuses `viewerCanRecordPotentialReadiness` directly
          (Mission 165 Part 4E: identical authorization to
          Potential/Readiness — manager, or admin/hr_partner, never self).
          Placed beside Potential/Readiness because all three are explicit
          human judgements a person records, never derived
          (docs/PRODUCT_CONSTITUTION.md §8/§12's "Potential, Readiness,
          Trajectory and Aspiration... never merged" — Talent Designation
          is the fourth member of that family, kept just as separate).
          docs/UX_ARCHITECTURE.md §7 places it exactly this way:
          "time-boxed cards with rationale and a review date, grouped by
          designation type, never as a permanent badge on the profile
          header" — this is the profile-body section that satisfies that,
          not a nav destination (§2's "Talent" nav item stays POST-MVP:
          the organisation-wide talent views it envisions are a different,
          larger surface than showing one person's own record here). */}
      <TalentDesignationSection profile={profile} />

      {/* Development, then Career — docs/UX_ARCHITECTURE.md §3.7/§3.8's own
          order, immediately after Role Fit. Both are basic capture only
          (docs/DOMAIN_DECISIONS.md "Development and Career Aspiration
          Capture Scope"): neither is a nav destination in its own right
          (DC-4), so both live here, reachable from the profile the way
          §3.7 explicitly allows for Development at MVP. */}
      <DevelopmentSection profile={profile} roles={roles} attributes={attributes} />
      <CareerAspirationsSection profile={profile} roles={roles} />
    </>
  );
}

function BackLink() {
  return (
    <Link to="/people" className={styles.back}>
      ← All people
    </Link>
  );
}
